import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, excelDate, seededRandom } from "../spec";

const SHEET = "BR Envíos";

const CLIENTES = ["Grupo Altamira", "Comercial Norte", "Farmacias Vida", "Hotel Mirador", "Café La Esquina"] as const;
const DESTINOS = [
  "Monterrey", "Saltillo", "San Luis Potosí", "Puebla", "Santiago de Querétaro",
  "Mérida", "San Pedro Garza García", "León",
] as const;
const ESTADOS = ["Entregado", "En tránsito", "En almacén"] as const;

const rnd = seededRandom(6060);
// Guía, Fecha, Cliente, Destino, Peso, Estado
const ENVIOS = Array.from({ length: 30 }, () => [
  "",
  excelDate("2026-02-02") + rnd.int(0, 55),
  rnd.pick(CLIENTES),
  rnd.pick(DESTINOS),
  rnd.int(1, 66) / 2,
  rnd.pick(ESTADOS),
] as [string, number, string, string, number, string])
  .sort((a, b) => a[1] - b[1])
  .map((row, i) => [`GU-${88001 + i}`, ...row.slice(1)] as typeof row);

// Deliberately not sorted: COINCIDIRX does not need it.
const CAJAS: [string, number][] = [
  ["Grande", 20],
  ["Chica", 5],
  ["Extra", 35],
  ["Mediana", 10],
];

type Envio = (typeof ENVIOS)[number];
// A guía from the middle of the weight ranking, so the answer is not trivially 1.
const MEDIA = [...ENVIOS].sort((a, b) => b[4] - a[4])[11][0];
const IN = { cliente: "Farmacias Vida", peso: 12.5, destino: "San", guia: MEDIA };

const caja = (peso: number) =>
  [...CAJAS].filter(([, cap]) => cap >= peso).sort((a, b) => a[1] - b[1])[0][0];
const primero = ENVIOS.findIndex((e) => e[2] === IN.cliente) + 1;
const ultimoIdx = ENVIOS.map((e) => e[2]).lastIndexOf(IN.cliente);
const destinoIdx = ENVIOS.findIndex((e) => e[3].toLowerCase().startsWith(IN.destino.toLowerCase())) + 1;
const pesoGuia = ENVIOS.find((e: Envio) => e[0] === IN.guia)![4];
const lugar = ENVIOS.filter((e) => e[4] > pesoGuia).length + 1;

export const spec: FuncionProjectSpec = {
  projectId: "funciones-coincidirx",
  sheets: [
    {
      name: SHEET,
      title: "Centro de envíos",
      subtitle: "Guías de febrero y marzo — responde en las celdas amarillas",
      tables: [
        {
          name: "Envios",
          anchor: "A4",
          headers: ["Guía", "Fecha", "Cliente", "Destino", "Peso", "Estado"],
          rows: ENVIOS,
          answerColumns: ["Caja"],
          formats: { Fecha: "dd/mm/yyyy" },
        },
        {
          name: "Cajas",
          anchor: "I4",
          headers: ["Tamaño", "Capacidad kg"],
          rows: CAJAS,
        },
      ],
      inputs: [
        { address: "M4", label: "Cliente", value: IN.cliente },
        { address: "M5", label: "Peso a enviar (kg)", value: IN.peso },
        { address: "M6", label: "Destino empieza con", value: IN.destino },
        { address: "M7", label: "Guía", value: IN.guia },
      ],
      answers: [
        { address: "M10", label: "Tarea 1 · Primer envío del cliente" },
        { address: "M11", label: "Tarea 2 · Último envío del cliente" },
        { address: "M12", label: "Tarea 3 · Caja para el peso" },
        { address: "M13", label: "Tarea 5 · Primer destino que empieza con" },
        { address: "M14", label: "Tarea 6 · Fecha del último envío", format: "dd/mm/yyyy" },
        { address: "M15", label: "Tarea 7 · Lugar por peso de la guía" },
      ],
    },
  ],
  checks: {
    1: { kind: "cell", sheet: SHEET, address: "M10", fns: ["XMATCH"], expected: primero },
    2: { kind: "cell", sheet: SHEET, address: "M11", fns: ["XMATCH"], expected: ultimoIdx + 1 },
    3: { kind: "cell", sheet: SHEET, address: "M12", fns: ["INDEX", "XMATCH"], expected: caja(IN.peso) },
    4: {
      kind: "column", sheet: SHEET, table: "Envios", column: "Caja", fns: ["INDEX", "XMATCH"],
      expected: ENVIOS.map((e) => caja(e[4])),
    },
    5: { kind: "cell", sheet: SHEET, address: "M13", fns: ["XMATCH"], expected: destinoIdx },
    6: { kind: "cell", sheet: SHEET, address: "M14", fns: ["INDEX", "XMATCH"], expected: ENVIOS[ultimoIdx][1] },
    7: { kind: "cell", sheet: SHEET, address: "M15", fns: ["XMATCH", "SORT"], expected: lugar },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-coincidirx",
  title: "COINCIDIRX: Centro de envíos",
  subtitle: "El COINCIDIR moderno: más modos y sin ordenar",
  description:
    "Coordinas un centro de envíos con 30 guías. Necesitas ubicar el primer y el último envío de un cliente, elegir la caja correcta según el peso y saber qué lugar ocupa un paquete entre los más pesados.\n\nCOINCIDIRX(valor, rango, modo_de_coincidencia, modo_de_búsqueda) hace lo mismo que COINCIDIR, pero es exacta por defecto, sabe buscar el siguiente mayor sin que la tabla esté ordenada, entiende comodines y puede buscar desde el final.",
  topics: [
    "Coincidencia exacta por defecto",
    "Buscar desde el final (modo_de_búsqueda -1)",
    "Siguiente mayor (modo 1) sin ordenar la tabla",
    "Comodines (modo 2)",
    "INDICE + COINCIDIRX",
    "Posición dentro de una lista ordenada",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Primer envío de un cliente",
      description:
        "En la hoja <strong>BR Envíos</strong>, celda <strong>M10</strong>: devuelve la <strong>posición</strong> del primer envío del cliente de <strong>M4</strong> en la columna Cliente.",
      solution: [
        `Selecciona ${F("M10")}.`,
        "A diferencia de COINCIDIR, COINCIDIRX es exacta por defecto: no hace falta el 0.",
        `Escribe ${F("=COINCIDIRX(M4,Envios[Cliente])")}.`,
      ],
      expectedResult: "Posición del primer envío de Farmacias Vida",
      finalFormula: "=COINCIDIRX(M4,Envios[Cliente])",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Último envío de un cliente",
      description:
        "En <strong>M11</strong>: devuelve la posición del <strong>último</strong> envío del mismo cliente.",
      solution: [
        `Selecciona ${F("M11")}.`,
        `El cuarto argumento, modo_de_búsqueda, con ${F("-1")} busca de abajo hacia arriba.`,
        `Escribe ${F("=COINCIDIRX(M4,Envios[Cliente],0,-1)")}.`,
      ],
      expectedResult: "Posición del último envío de Farmacias Vida",
      finalFormula: "=COINCIDIRX(M4,Envios[Cliente],0,-1)",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: ¿Qué caja uso?",
      description: conditions(
        "En <strong>M12</strong>: devuelve el <strong>Tamaño</strong> de caja para el peso de <strong>M5</strong>, usando la tabla <strong>Cajas</strong> (I4):",
        [
          "La caja debe tener una capacidad <strong>igual o mayor</strong> al peso",
          "De las que alcanzan, la <strong>más chica</strong>",
          "La tabla Cajas <strong>no está ordenada</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("M12")}.`,
        `Modo de coincidencia ${F("1")}: exacta o el siguiente valor <strong>mayor</strong>. COINCIDIRX lo encuentra aunque la tabla esté desordenada.`,
        `Escribe ${F("=INDICE(Cajas[Tamaño],COINCIDIRX(M5,Cajas[Capacidad kg],1))")}.`,
      ],
      expectedResult: "Grande (12.5 kg cabe en la de 20)",
      finalFormula: "=INDICE(Cajas[Tamaño],COINCIDIRX(M5,Cajas[Capacidad kg],1))",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Caja para cada envío",
      description: "En la tabla <strong>Envios</strong>, columna <strong>Caja</strong>: aplica la misma regla de la Tarea 3 al <strong>Peso</strong> de cada envío.",
      solution: [
        `Selecciona ${F("G5")}.`,
        `Escribe ${F("=INDICE(Cajas[Tamaño],COINCIDIRX([@Peso],Cajas[Capacidad kg],1))")}.`,
      ],
      expectedResult: "Chica, Mediana, Grande o Extra",
      finalFormula: "=INDICE(Cajas[Tamaño],COINCIDIRX([@Peso],Cajas[Capacidad kg],1))",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Destino que empieza con…",
      description:
        "En <strong>M13</strong>: devuelve la posición del primer envío cuyo <strong>Destino empiece</strong> con el texto de <strong>M6</strong> (por ejemplo \"San Luis Potosí\"). Ojo: \"Saltillo\" empieza con \"Sa\" pero no con \"San\".",
      solution: [
        `Selecciona ${F("M13")}.`,
        `Con modo de coincidencia ${F("2")}, COINCIDIRX entiende comodines: ${F('M6&"*"')} es \"San seguido de cualquier cosa\".`,
        `Escribe ${F('=COINCIDIRX(M6&"*",Envios[Destino],2)')}.`,
      ],
      expectedResult: "Posición del primer destino que empieza con San",
      finalFormula: '=COINCIDIRX(M6&"*",Envios[Destino],2)',
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Fecha del último envío",
      description:
        "En <strong>M14</strong>: devuelve la <strong>Fecha</strong> del último envío del cliente de <strong>M4</strong>. Combina INDICE con el COINCIDIRX de la Tarea 2.",
      solution: [
        `Selecciona ${F("M14")}.`,
        `Escribe ${F("=INDICE(Envios[Fecha],COINCIDIRX(M4,Envios[Cliente],0,-1))")}.`,
      ],
      expectedResult: "Fecha del envío más reciente de Farmacias Vida",
      finalFormula: "=INDICE(Envios[Fecha],COINCIDIRX(M4,Envios[Cliente],0,-1))",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: ¿Qué lugar ocupa por peso?",
      description: conditions(
        "En <strong>M15</strong>: di qué <strong>lugar</strong> ocupa la guía de <strong>M7</strong> si ordenas todos los envíos del <strong>más pesado al más ligero</strong> (1 = el más pesado):",
        [
          "Obtén el peso de esa guía con <strong>BUSCARX</strong>",
          "Ordena todos los pesos de mayor a menor con <strong>ORDENAR</strong>",
          "Busca la posición del peso en la lista ordenada con <strong>COINCIDIRX</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("M15")}.`,
        `${F("ORDENAR(Envios[Peso],,-1)")} devuelve los pesos de mayor a menor; la coma doble deja vacío el argumento del número de columna.`,
        `Escribe ${F("=COINCIDIRX(BUSCARX(M7,Envios[Guía],Envios[Peso]),ORDENAR(Envios[Peso],,-1))")}.`,
      ],
      expectedResult: "El lugar de la guía en el ranking de peso",
      finalFormula: "=COINCIDIRX(BUSCARX(M7,Envios[Guía],Envios[Peso]),ORDENAR(Envios[Peso],,-1))",
    },
  ],
};
